/**
 * Pure tree logic for the chart-of-accounts workbench.
 *
 * Kept out of the component so the parts that are easy to get subtly wrong —
 * shift-range selection across a collapsed tree, filtering that must keep
 * ancestors visible, subtree descent — are unit-testable without rendering.
 */

export interface TreeNode {
  id: string;
  code: string;
  name: string;
  type: string;
  children: TreeNode[];
}

export interface BalanceTreeNode extends TreeNode {
  balance: number;
  subtreeBalance?: number;
  children: BalanceTreeNode[];
}

/** Preserve direct balances; group rows display their own postings plus all descendants. */
export function withSubtreeBalances<T extends BalanceTreeNode>(nodes: T[]): T[] {
  return nodes.map((node) => {
    const children = withSubtreeBalances(node.children as T[]);
    return {
      ...node, children,
      subtreeBalance: node.balance + children.reduce((sum, child) => sum + (child.subtreeBalance ?? child.balance), 0),
    };
  });
}

/** Depth-first in render order: the basis for shift-range selection. */
export function flattenTree<T extends TreeNode>(nodes: T[], out: T[] = []): T[] {
  for (const node of nodes) {
    out.push(node);
    flattenTree(node.children as T[], out);
  }
  return out;
}

/**
 * Filter by code or name, keeping a matching node's ancestors so a hit deep in
 * the chart is still reachable rather than orphaned out of the tree.
 */
export function filterTree<T extends TreeNode>(nodes: T[], query: string): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return nodes;

  return nodes.reduce<T[]>((acc, node) => {
    const children = filterTree(node.children as T[], needle);
    const hit =
      node.code.toLowerCase().includes(needle) || node.name.toLowerCase().includes(needle);
    if (hit || children.length > 0) acc.push({ ...node, children } as T);
    return acc;
  }, []);
}

export function collectCodes(nodes: TreeNode[], out = new Set<string>()): Set<string> {
  for (const node of nodes) {
    out.add(node.code);
    collectCodes(node.children, out);
  }
  return out;
}

/**
 * Selection after a click.
 *
 * A shift-click extends from the last clicked row to this one over the FLATTENED
 * order, which is what the accountant sees — so a range never silently picks up
 * rows hidden inside a collapsed branch that they cannot see.
 */
export function nextSelection(
  current: ReadonlySet<string>,
  flat: readonly TreeNode[],
  id: string,
  options: { shiftKey: boolean; anchorId: string | null }
): Set<string> {
  const next = new Set(current);

  if (options.shiftKey && options.anchorId) {
    const from = flat.findIndex((node) => node.id === options.anchorId);
    const to = flat.findIndex((node) => node.id === id);
    if (from !== -1 && to !== -1) {
      const [start, end] = from < to ? [from, to] : [to, from];
      for (let i = start; i <= end; i += 1) {
        const row = flat[i];
        if (row) next.add(row.id);
      }
      return next;
    }
  }

  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** Is this node, or anything under it, admitted by the current filter? */
export function subtreeVisible(node: TreeNode, visibleIds: ReadonlySet<string> | null): boolean {
  if (!visibleIds) return true;
  if (visibleIds.has(node.id)) return true;
  return node.children.some((child) => subtreeVisible(child, visibleIds));
}

/**
 * Ids of a node and everything beneath it.
 *
 * Dropping a branch onto one of its own descendants would detach it from the
 * tree, so the drop target has to be checked against this set before the move
 * is offered.
 */
export function subtreeIds(node: TreeNode, out = new Set<string>()): Set<string> {
  out.add(node.id);
  for (const child of node.children) subtreeIds(child, out);
  return out;
}

/** Would dropping `dragged` onto `target` create a cycle? */
export function isInvalidDrop(dragged: TreeNode, target: TreeNode): boolean {
  return subtreeIds(dragged).has(target.id);
}
